import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import StatusContext from "common/store/StatusContext";
import { useAnnouncement } from "../hooks/useAnnouncement";
import { AnnouncementData } from "../types/AnnoucementData";
import { Container } from "react-bootstrap";
import { AnnouncementModal } from "./AnnouncementModal";
import { safeApiDate } from "utils/DateTools";

export const AnnouncementPanel = () => {
  const { setErrorMessage } = useContext(StatusContext);
  const { getAnnouncements } = useAnnouncement();
  const [data, setData] = useState<AnnouncementData[]>([]);
  const [targetData, setTargetData] = useState<AnnouncementData>();

  const loadData = async () => {
    setErrorMessage("");
    const res = await getAnnouncements();
    if (!res.succeeded) {
      setErrorMessage(res.msg);
      return;
    }
    setData(res.data);
  };

  useEffect(() => {
    const f = async () => await loadData();
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnnouncementClick = (announcement: AnnouncementData) => {
    setTargetData(announcement);
  };

  return (
    <SRootDiv>
      <STitle className="fs-5 fw-bold text-primary mb-3">お知らせ</STitle>
      {data.length === 0 ? (
        <SNoDataMessage className="fs-6 text-muted">現在お知らせはありません</SNoDataMessage>
      ) : (
        <SAnnouncementList>
          {data.map((x, index) => (
            <SAnnouncementItem key={index}>
              <SAnnouncementLink
                onClick={() => handleAnnouncementClick(x)}
                className="text-decoration-none"
              >
                <SAnnouncementTitle className="fs-6 text-dark mb-1">
                  {(() => {
                    if (!x.publishDate) return null;
                    const publishDate = safeApiDate(x.publishDate);
                    const today = new Date();
                    const diffTime = today.getTime() - publishDate.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    if (diffDays <= 7) {
                      return <SNewBadge className="badge bg-danger me-2">NEW</SNewBadge>;
                    }
                    return null;
                  })()}
                  {x.title}
                </SAnnouncementTitle>
                {x.publishDate && (
                  <div className="small text-muted">
                    {`(${(() => {
                      return safeApiDate(x.publishDate).toLocaleDateString("ja-JP", {
                        month: "numeric",
                        day: "numeric",
                      });
                    })()})`}
                  </div>
                )}
              </SAnnouncementLink>
            </SAnnouncementItem>
          ))}
        </SAnnouncementList>
      )}
      {targetData && (
        <AnnouncementModal
          open={targetData !== undefined}
          data={targetData}
          closeClicked={() => {
            setTargetData(undefined);
          }}
        />
      )}
    </SRootDiv>
  );
};

const SRootDiv = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;
  margin-top: 1em;
  margin-left: auto;
  margin-right: auto;
`;

const STitle = styled.h2`
  margin: 0;
  text-align: center;
`;

const SNoDataMessage = styled.p`
  text-align: center;
  font-style: italic;
`;

const SAnnouncementList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SAnnouncementItem = styled.div`
  border-bottom: 1px solid #e9ecef;
  transition: all 0.2s ease-in-out;
  text-align: center;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #007bff;
    transform: translateY(-1px);
  }
`;

const SAnnouncementLink = styled.div`
  padding: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1rem;
  width: 100%;
`;

const SAnnouncementTitle = styled.div`
  font-weight: 500;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: #0056b3 !important;
  }
`;

const SNewBadge = styled.span`
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
